import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import { consultationApi, notificationApi } from '../api/consultationApi';
import { registerPush } from '../utils/pushClient';

const RealtimeContext = createContext(null);

export function useRealtime() {
  return useContext(RealtimeContext) || {};
}

function socketUrl() {
  if (process.env.REACT_APP_SOCKET_URL) return process.env.REACT_APP_SOCKET_URL;
  // In CRA dev (frontend on :3000) the websocket proxy is unreliable, so talk
  // to the backend (:5000) directly. Socket.IO CORS is open on the server.
  const { protocol, hostname, port } = window.location;
  if (port === '3000') return `${protocol}//${hostname}:5000`;
  return window.location.origin;
}

export const RealtimeProvider = ({ children }) => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [currentUser, setCurrentUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [incomingCall, setIncomingCall] = useState(null);
  const socketRef = useRef(null);

  // Track login/logout by polling localStorage token (login flow updates it).
  useEffect(() => {
    const check = () => {
      const t = localStorage.getItem('token');
      setToken((prev) => (prev !== t ? t : prev));
    };
    const interval = setInterval(check, 1500);
    window.addEventListener('storage', check);
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', check);
    };
  }, []);

  const refreshNotifications = useCallback(async () => {
    try {
      const { data } = await notificationApi.list(30);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const markRead = useCallback(async (id) => {
    try {
      await notificationApi.markRead(id);
      setNotifications((prev) => prev.map((n) => (n._id === id ? { ...n, read: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (_) { /* ignore */ }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await notificationApi.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (_) { /* ignore */ }
  }, []);

  const clearIncoming = useCallback(() => setIncomingCall(null), []);

  const acceptIncoming = useCallback(async () => {
    if (!incomingCall) return;
    const bookingId = incomingCall.bookingId;
    setIncomingCall(null);
    try {
      await consultationApi.answerCall(bookingId);
    } catch (_) { /* room will re-validate */ }
    navigate(`/video-consultation/${bookingId}?answer=1`);
  }, [incomingCall, navigate]);

  const declineIncoming = useCallback(async () => {
    if (!incomingCall) return;
    const bookingId = incomingCall.bookingId;
    setIncomingCall(null);
    try {
      await consultationApi.declineCall(bookingId, 'Declined');
    } catch (_) { /* ignore */ }
  }, [incomingCall]);

  // Establish the realtime socket + load initial state whenever auth changes.
  useEffect(() => {
    if (!token) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setCurrentUser(null);
      setNotifications([]);
      setUnreadCount(0);
      setIncomingCall(null);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      // Load who am I (id + role) for gating UI.
      try {
        const res = await fetch('/api/auth/profile', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const body = await res.json();
          if (!cancelled) setCurrentUser(body.user);
        }
      } catch (_) { /* ignore */ }
    })();

    refreshNotifications();
    registerPush();

    const socket = io(`${socketUrl()}/realtime`, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('connect', () => console.log('[realtime] connected', socket.id));
    socket.on('connect_error', (err) => console.warn('[realtime] connect_error:', err.message));

    socket.on('notification:new', (n) => {
      setNotifications((prev) => [n, ...prev].slice(0, 50));
      setUnreadCount((c) => c + 1);
      toast.info(`${n.title}`);
    });

    socket.on('call:incoming', (payload) => {
      console.log('[realtime] call:incoming', payload);
      setIncomingCall(payload);
    });

    socket.on('call:cancelled', (payload) => {
      setIncomingCall((prev) => (prev && prev.bookingId === payload.bookingId ? null : prev));
    });

    return () => {
      cancelled = true;
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, refreshNotifications]);

  // Service worker asks us to navigate (e.g. after tapping a call notification).
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const handler = (event) => {
      if (event.data && event.data.type === 'NAVIGATE' && event.data.url) {
        const url = event.data.url.replace(window.location.origin, '');
        navigate(url);
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [navigate]);

  const value = {
    currentUser,
    notifications,
    unreadCount,
    refreshNotifications,
    markRead,
    markAllRead,
    incomingCall,
    acceptIncoming,
    declineIncoming,
    clearIncoming,
    socket: socketRef,
  };

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
};

export default RealtimeContext;
